import { useTranslation as useCrmTranslation } from '@topolo-io/ui-kit';
import { type JSX, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  createTopoloLazyComponent,
  createTopoloLazyRoute,
  TopoloAppRoot,
  useAuth,
  type AppRoute,
} from '@topolo-io/app-shell';
import {
  TopoloI18nProvider,
  topoloServiceUrl,
  ConfirmDialogHost,
  ToastViewport,
  TopoloContentSkeleton,
  type TopoloContentSkeletonVariant,
} from '@topolo-io/ui-kit';
import { CrmCopyBoundary } from './components/CrmCopyBoundary';
import { queryClient } from './lib/queryClient';
import { authClient } from './lib/topolo-auth';
import { archiveCrmWorkspace } from './lib/crm-workspaces';
import { CRM_COPY } from './lib/crm-copy';
import { CRM_WORKSPACE_STORAGE_KEY } from './utils/api/api-client';

const ContactsPageRoute = createTopoloLazyRoute({
  loader: () => import('./components/ContactsPage'),
  fallback: <RouteLoadingFallback variant='card-grid' />,
});
const LoginRedirectRoute = createTopoloLazyRoute({
  loader: () => import('./components/AuthProvider').then(m => ({ default: m.LoginRedirect })),
  fallback: <LoadingScreen />,
});
const LayoutRoute = createTopoloLazyComponent<{ children: ReactNode }>({
  loader: () => import('./components/Layout').then(m => ({ default: m.Layout })),
  fallback: <LoadingScreen />,
});
const AuthCallbackPageRoute = createTopoloLazyRoute({
  loader: () => import('./components/AuthCallbackPage'),
  fallback: <LoadingScreen />,
});
const ContactViewRoute = createTopoloLazyRoute({
  loader: () => import('./components/ContactView'),
  fallback: <RouteLoadingFallback variant='detail' />,
});
const CompaniesPageRoute = createTopoloLazyRoute({
  loader: () => import('./components/CompaniesPage'),
  fallback: <RouteLoadingFallback variant='card-grid' />,
});
const CompanyViewRoute = createTopoloLazyRoute({
  loader: () => import('./components/CompanyView'),
  fallback: <RouteLoadingFallback variant='detail' />,
});
const DealsPageRoute = createTopoloLazyRoute({
  loader: () => import('./components/DealsPage'),
  fallback: <RouteLoadingFallback variant='card-grid' />,
});
const DealViewRoute = createTopoloLazyRoute({
  loader: () => import('./components/DealView'),
  fallback: <RouteLoadingFallback variant='detail' />,
});
const PipelinePageRoute = createTopoloLazyRoute({
  loader: () => import('./components/PipelinePage'),
  fallback: <RouteLoadingFallback variant='board' />,
});
const ImportPageRoute = createTopoloLazyRoute({
  loader: () => import('./components/ImportPage'),
  fallback: <RouteLoadingFallback variant='form' />,
});
const TasksPageRoute = createTopoloLazyRoute({
  loader: () => import('./components/TasksPage'),
  fallback: <RouteLoadingFallback variant='list' />,
});
const InboxPageRoute = createTopoloLazyRoute({
  loader: () => import('./components/InboxPage'),
  fallback: <RouteLoadingFallback variant='list' />,
});
const DashboardPageRoute = createTopoloLazyRoute({
  loader: () => import('./components/DashboardPage'),
  fallback: <RouteLoadingFallback variant='dashboard' />,
});
const PortalConnectionsPageRoute = createTopoloLazyRoute({
  loader: () => import('./components/PortalConnectionsPage'),
  fallback: <RouteLoadingFallback variant='card-grid' />,
});
const PortalLeadsPageRoute = createTopoloLazyRoute({
  loader: () => import('./components/PortalLeadsPage'),
  fallback: <RouteLoadingFallback variant='table' />,
});
const ObjectRecordsPageRoute = createTopoloLazyRoute({
  loader: () => import('./components/objects/ObjectRecordsPage'),
  fallback: <RouteLoadingFallback variant='table' />,
});
const ObjectRecordViewRoute = createTopoloLazyRoute({
  loader: () => import('./components/objects/ObjectRecordView'),
  fallback: <RouteLoadingFallback variant='detail' />,
});
const SettingsPageRoute = createTopoloLazyRoute({
  loader: () => import('./components/SettingsPage').then(m => ({ default: m.SettingsPage })),
  fallback: <RouteLoadingFallback variant='form' />,
});
const GettingStartedPageRoute = createTopoloLazyRoute({
  loader: () => import('./components/GettingStartedPage'),
  fallback: <RouteLoadingFallback variant='actions' />,
});
import { DEFAULT_MODULE_SETTINGS, getDefaultModulePath } from './lib/moduleSettings';
import { useOrgSettingsQuery } from './hooks/useQueries';
import type { CrmModuleKey } from './types/api';
import { LandingPage, TopoloLoadingScreen, TopoloAppLoadingScaffold } from '@topolo-io/app-shell';
import { setupBugReporterErrorCapture } from '@topolo-io/ui-kit';
import { getAppId } from '@topolo-io/app-shell';

// Set up global error capture for bug reporting
setupBugReporterErrorCapture();

const CRM_APP_ID = getAppId();

// During an app-switcher hand-off the launcher + orbit set a short-lived shared
// cookie (`topolo_handoff_active`, ~20s). While it's fresh we render the
// canonical orbit so the hand-off is one continuous spinner. On a plain
// refresh / direct load / in-app navigation (no hand-off) we show CRM's own
// scaffold — the orbit must NEVER appear inside the app outside a hand-off.
// Mirrors @topolo-io/ui-kit's internal hasRecentHandoffContinuity (not a
// public export).
function hasRecentHandoffContinuity(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const m = document.cookie.split(/;\s*/).find(p => p.startsWith('topolo_handoff_active='));
    if (!m) return false;
    const ts = Number.parseInt(decodeURIComponent(m.slice('topolo_handoff_active='.length)), 10);
    return Number.isFinite(ts) && Date.now() - ts <= 20_000;
  } catch {
    return false;
  }
}

// Auth/session bootstrap + route loader (route unknown at this level). On a
// hand-off it's the orbit (continuous with the launcher); otherwise the
// canonical app-shell skeleton. In-view data loads use per-view content
// skeletons (TopoloContentSkeleton) inside the mounted shell.
function LoadingScreen() {
  if (hasRecentHandoffContinuity()) {
    return <TopoloLoadingScreen appName='CRM' appId={CRM_APP_ID} />;
  }
  return <TopoloAppLoadingScaffold appName='CRM' appId={CRM_APP_ID} />;
}

// In-shell content skeleton for lazy route chunk loads. The mounted app shell
// (sidebar) stays; only the content region shows a route-shaped skeleton instead
// of a blank flash. Variant is matched to each page's own loaded skeleton.
//
// Only routes that render INSIDE the shell may use it. The three surfaces that
// mount before the shell exists — the layout chunk itself, the login redirect
// and the auth callback — use LoadingScreen instead, so the sidebar does not
// vanish and return between the auth bootstrap and the first page. Using a
// bare content skeleton there was the largest of the scaffold changes a cold
// load used to walk through.
function RouteLoadingFallback({
  variant = 'dashboard',
}: {
  variant?: TopoloContentSkeletonVariant;
}) {
  return <TopoloContentSkeleton variant={variant} />;
}

/**
 * Dynamic home for `<TopoloAppRoot>` — the first active module path, derived from
 * the org's module settings + the user's role. Runs inside the router + auth tree.
 */
function useCrmHome() {
  const { user, isAuthenticated } = useAuth();
  const { data } = useOrgSettingsQuery({ enabled: isAuthenticated });
  const moduleSettings = data?.org_settings.module_settings || DEFAULT_MODULE_SETTINGS;
  const roleDefaults = data?.org_settings.role_defaults || [];
  return {
    path: getDefaultModulePath(moduleSettings, roleDefaults, user?.role),
    loading: false,
  };
}

/** Hides a route when its module is disabled, redirecting to the first active module. */
function ModuleRoute({
  moduleKey,
  children,
}: {
  moduleKey: CrmModuleKey;
  children: ReactNode;
}): JSX.Element {
  const { data } = useOrgSettingsQuery();
  const moduleSettings = data?.org_settings.module_settings || DEFAULT_MODULE_SETTINGS;
  const roleDefaults = data?.org_settings.role_defaults || [];
  const { user } = useAuth();

  if (moduleSettings[moduleKey] === false) {
    return <Navigate to={getDefaultModulePath(moduleSettings, roleDefaults, user?.role)} replace />;
  }
  return <>{children}</>;
}

/** `/login` — send already-authenticated users home, otherwise show the login screen. */
function LoginRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const { path, loading } = useCrmHome();
  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) return loading ? <LoadingScreen /> : <Navigate to={path} replace />;
  return LoginRedirectRoute.element;
}

function CrmShell({ children }: { children: ReactNode }): JSX.Element {
  const Layout = LayoutRoute.Component;
  return <Layout>{children}</Layout>;
}

const baseRoutes: AppRoute[] = [
  {
    path: '/inbox',
    element: <ModuleRoute moduleKey='inbox'>{InboxPageRoute.element}</ModuleRoute>,
    preload: InboxPageRoute.preload,
    loading: InboxPageRoute.loading,
  },
  {
    path: '/contacts',
    element: <ModuleRoute moduleKey='contacts'>{ContactsPageRoute.element}</ModuleRoute>,
    preload: ContactsPageRoute.preload,
    loading: ContactsPageRoute.loading,
  },
  {
    path: '/contacts/:contactId',
    element: <ModuleRoute moduleKey='contacts'>{ContactViewRoute.element}</ModuleRoute>,
    preload: ContactViewRoute.preload,
    loading: ContactViewRoute.loading,
  },
  {
    path: '/companies',
    element: <ModuleRoute moduleKey='companies'>{CompaniesPageRoute.element}</ModuleRoute>,
    preload: CompaniesPageRoute.preload,
    loading: CompaniesPageRoute.loading,
  },
  {
    path: '/companies/:companyId',
    element: <ModuleRoute moduleKey='companies'>{CompanyViewRoute.element}</ModuleRoute>,
    preload: CompanyViewRoute.preload,
    loading: CompanyViewRoute.loading,
  },
  {
    path: '/deals',
    element: <ModuleRoute moduleKey='deals'>{DealsPageRoute.element}</ModuleRoute>,
    preload: DealsPageRoute.preload,
    loading: DealsPageRoute.loading,
  },
  {
    path: '/deals/:dealId',
    element: <ModuleRoute moduleKey='deals'>{DealViewRoute.element}</ModuleRoute>,
    preload: DealViewRoute.preload,
    loading: DealViewRoute.loading,
  },
  {
    path: '/objects/:objectKey',
    element: ObjectRecordsPageRoute.element,
    preload: ObjectRecordsPageRoute.preload,
    loading: ObjectRecordsPageRoute.loading,
  },
  {
    path: '/objects/:objectKey/:recordId',
    element: ObjectRecordViewRoute.element,
    preload: ObjectRecordViewRoute.preload,
    loading: ObjectRecordViewRoute.loading,
  },
  {
    path: '/import',
    element: ImportPageRoute.element,
    preload: ImportPageRoute.preload,
    loading: ImportPageRoute.loading,
  },
  {
    path: '/pipeline',
    element: <ModuleRoute moduleKey='pipeline'>{PipelinePageRoute.element}</ModuleRoute>,
    preload: PipelinePageRoute.preload,
    loading: PipelinePageRoute.loading,
  },
  {
    path: '/pipeline/:pipelineId',
    element: <ModuleRoute moduleKey='pipeline'>{PipelinePageRoute.element}</ModuleRoute>,
    preload: PipelinePageRoute.preload,
    loading: PipelinePageRoute.loading,
  },
  {
    path: '/tasks',
    element: <ModuleRoute moduleKey='tasks'>{TasksPageRoute.element}</ModuleRoute>,
    preload: TasksPageRoute.preload,
    loading: TasksPageRoute.loading,
  },
  {
    path: '/dashboard',
    element: DashboardPageRoute.element,
    preload: DashboardPageRoute.preload,
    loading: DashboardPageRoute.loading,
  },
  {
    path: '/portal-connections',
    element: (
      <ModuleRoute moduleKey='portal_connections'>{PortalConnectionsPageRoute.element}</ModuleRoute>
    ),
    preload: PortalConnectionsPageRoute.preload,
    loading: PortalConnectionsPageRoute.loading,
  },
  {
    path: '/portal-leads',
    element: <ModuleRoute moduleKey='portal_leads'>{PortalLeadsPageRoute.element}</ModuleRoute>,
    preload: PortalLeadsPageRoute.preload,
    loading: PortalLeadsPageRoute.loading,
  },
  {
    path: '/settings',
    element: <Navigate to='/settings/organization' replace />,
  },
  {
    path: '/settings/:section',
    element: SettingsPageRoute.element,
    preload: SettingsPageRoute.preload,
    loading: SettingsPageRoute.loading,
  },
  {
    path: '/getting-started',
    element: GettingStartedPageRoute.element,
    preload: GettingStartedPageRoute.preload,
    loading: GettingStartedPageRoute.loading,
  },
];
const routes: AppRoute[] = baseRoutes;

function CrmRoot() {
  useCrmTranslation();
  const queryClient = useQueryClient();
  return (
    <TopoloAppRoot
      appName='CRM'
      appId={CRM_APP_ID}
      auth={authClient}
      routes={routes}
      useHomePath={useCrmHome}
      publicLanding={<LandingPage appId={CRM_APP_ID} />}
      shellComponent={CrmShell}
      loginElement={<LoginRoute />}
      callbackElement={AuthCallbackPageRoute.element}
      workspaceResource={{
        storageKey: CRM_WORKSPACE_STORAGE_KEY,
        required: true,
        canCreate: true,
        label: CRM_COPY.workspace.label,
        createLabel: CRM_COPY.workspace.create,
        onSelect: () => queryClient.clear(),
        onDelete: async workspaceId => {
          await archiveCrmWorkspace(workspaceId);
          queryClient.clear();
        },
      }}
      loadingFallback={<LoadingScreen />}
      onAuthCleared={() => queryClient.clear()}
      onOrgChange={() => queryClient.clear()}
    />
  );
}

function App() {
  const showDevtools =
    import.meta.env.DEV &&
    !(
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('visualQa') === '1'
    );

  return (
    <TopoloI18nProvider appId="app_topolo_crm" revision={import.meta.env.VITE_TOPOLO_REVISION} catalogManifestUrl={topoloServiceUrl('localize', '/api/catalog-manifest')} getAccessToken={() => authClient.getAccessToken()}>
      <CrmCopyBoundary>
    <QueryClientProvider client={queryClient}>
      <CrmRoot />
      <ConfirmDialogHost />
      <ToastViewport />
      {/* React Query DevTools - only in development */}
      {showDevtools && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
      </CrmCopyBoundary>
    </TopoloI18nProvider>
  );
}

export default App;
